/**
 * Identifies note-related activities across the activity feed system.
 * Centralizes the logic that was previously duplicated in WhatsNewContainer,
 * ActivityFeedContainer, useActivityDeepLink, and useActivityPlayerNames.
 * Handles both explicit note types (NOTE_CREATED, NOTE_SHARED) and the
 * generic CONTENT_SHARED type used by auto-share when contentType is "NOTE" (SE-3990).
 * @module utils/isNoteActivity
 */

/**
 * Activity types whose targetId points to a note rather than a player.
 * For these types, targetId must NOT be used as a player ID fallback (SE-3989).
 */
export const NOTE_ACTIVITY_TYPES = new Set([
  "NOTE_CREATED",
  "NOTE_SHARED",
  "NOTE_VISIBILITY_CHANGED",
]);

/**
 * Determines whether an activity represents a note-related action.
 * Covers both explicit note types and CONTENT_SHARED with contentType "NOTE"
 * from the auto-share flow (SE-3990).
 * @param activityType - The activity type code
 * @param metadata - Activity metadata that may contain contentType
 * @returns Whether the activity's targetId points to a note
 */
export const isNoteActivity = (
  activityType: string,
  metadata: Record<string, unknown> | null | undefined
): boolean =>
  NOTE_ACTIVITY_TYPES.has(activityType) ||
  (activityType === "CONTENT_SHARED" && metadata?.contentType === "NOTE");
